import { useState, useEffect } from 'react'
import { DashboardLayout } from '@/components/layout/DashboardLayout'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Switch } from '@/components/ui/switch'
import { Label } from '@/components/ui/label'
import { Search, Plus, Eye, Edit, Globe, Building2, FileText, Users, TrendingUp, Trees, Heart } from 'lucide-react'
import { supabase } from '@/integrations/supabase/client'
import { useToast } from '@/hooks/use-toast'
import ContentEditor from '@/components/admin/ContentEditor'
import LivePreview from '@/components/admin/LivePreview'
import VisualPageBuilder from '@/components/admin/VisualPageBuilder'
import { ErrorBoundary } from '@/components/ui/error-boundary'
import { useNavigate } from 'react-router-dom'

interface LandingPage {
  id: string
  name: string
  slug: string
  is_active: boolean
  is_default: boolean
  created_at: string
  updated_at: string
  tenant_id: string
  template_id: string | null
  content_data: any
  referrer_message: string
  tenant?: { name: string }
  template?: { name: string }
}

interface Template {
  id: string
  name: string
  description: string | null
  category: string
  is_active: boolean
  template_data: any
  preview_image_url: string | null
}

interface SystemStats {
  totalPages: number
  activePages: number
  totalTemplates: number
  totalTenants: number
  treesPlanted: number
  donationsTotal: number
}

const SystemLandingPages = () => {
  const navigate = useNavigate()
  const [landingPages, setLandingPages] = useState<LandingPage[]>([])
  const [filteredPages, setFilteredPages] = useState<LandingPage[]>([])
  const [templates, setTemplates] = useState<Template[]>([])
  const [tenants, setTenants] = useState<Array<{id: string, name: string}>>([])
  const [stats, setStats] = useState<SystemStats>({
    totalPages: 0,
    activePages: 0,
    totalTemplates: 0,
    totalTenants: 0,
    treesPlanted: 0,
    donationsTotal: 0
  })
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState<string>('all')
  const [selectedTenant, setSelectedTenant] = useState<string>('all')
  const [loading, setLoading] = useState(true)
  
  // Template creation
  const [isCreateTemplateOpen, setIsCreateTemplateOpen] = useState(false)
  const [newTemplate, setNewTemplate] = useState({
    name: '',
    description: '',
    category: 'general'
  })

  // Template editing
  const [isEditTemplateOpen, setIsEditTemplateOpen] = useState(false)
  const [editingTemplate, setEditingTemplate] = useState<Template | null>(null)
  const [templateForm, setTemplateForm] = useState({
    name: '',
    description: '',
    category: 'general',
    is_active: true,
    preview_image_url: '',
    template_data: '{}'
  })

  // Page editing
  const [isEditPageOpen, setIsEditPageOpen] = useState(false)
  const [editingPage, setEditingPage] = useState<LandingPage | null>(null)
  const [pageBuffer, setPageBuffer] = useState<Partial<LandingPage>>({})

  const { toast } = useToast()

  useEffect(() => {
    fetchData()
  }, [])

  useEffect(() => {
    filterPages()
  }, [landingPages, searchTerm, statusFilter, selectedTenant])

  const fetchData = async () => {
    try {
      setLoading(true)

      // Fetch all landing pages with tenant and template information
      const { data: pagesData, error: pagesError } = await supabase
        .from('tenant_landing_pages')
        .select(`
          *,
          tenants:tenant_id (name),
          landing_page_templates:template_id (name)
        `)
        .order('created_at', { ascending: false })

      if (pagesError) throw pagesError

      // Fetch templates
      const { data: templatesData, error: templatesError } = await supabase
        .from('landing_page_templates')
        .select('*')
        .order('name')

      if (templatesError) throw templatesError

      // Fetch tenants
      const { data: tenantsData, error: tenantsError } = await supabase
        .from('tenants')
        .select('id, name')
        .eq('status', 'active')
        .order('name')

      if (tenantsError) throw tenantsError

      // Fetch environmental impact for trees and donations
      const { data: envData, error: envError } = await supabase
        .from('environmental_impact')
        .select('trees_planted, donation_amount')

      if (envError) console.warn('Could not fetch environmental data:', envError)

      setLandingPages(pagesData || [])
      setTemplates(templatesData || [])
      setTenants(tenantsData || [])

      // Calculate stats
      const totalPages = pagesData?.length || 0
      const activePages = pagesData?.filter(page => page.is_active).length || 0
      const totalTemplates = templatesData?.length || 0
      const totalTenants = tenantsData?.length || 0
      const treesPlanted = envData?.reduce((sum, item) => sum + (item.trees_planted || 0), 0) || 0
      const donationsTotal = envData?.reduce((sum, item) => sum + (parseFloat(item.donation_amount?.toString() || '0') || 0), 0) || 0

      setStats({
        totalPages,
        activePages,
        totalTemplates,
        totalTenants,
        treesPlanted,
        donationsTotal
      })

    } catch (error: any) {
      console.error('Error fetching landing pages data:', error)
      toast({
        title: "Fehler beim Laden der Daten",
        description: error.message,
        variant: "destructive"
      })
    } finally {
      setLoading(false)
    }
  }

  const filterPages = () => {
    let filtered = landingPages

    if (searchTerm) {
      filtered = filtered.filter(page =>
        page.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        page.slug.toLowerCase().includes(searchTerm.toLowerCase()) ||
        page.tenant?.name?.toLowerCase().includes(searchTerm.toLowerCase())
      )
    }

    if (statusFilter !== 'all') {
      if (statusFilter === 'active') {
        filtered = filtered.filter(page => page.is_active)
      } else if (statusFilter === 'inactive') {
        filtered = filtered.filter(page => !page.is_active)
      } else if (statusFilter === 'default') {
        filtered = filtered.filter(page => page.is_default)
      }
    }

    if (selectedTenant !== 'all') {
      filtered = filtered.filter(page => page.tenant_id === selectedTenant)
    }

    setFilteredPages(filtered)
  }

  const createTemplate = async () => {
    try {
      const { error } = await supabase
        .from('landing_page_templates')
        .insert([{
          name: newTemplate.name,
          description: newTemplate.description,
          category: newTemplate.category,
          template_data: {}
        }])

      if (error) throw error

      toast({
        title: "Template erstellt",
        description: "Das neue Template wurde erfolgreich erstellt."
      })

      setIsCreateTemplateOpen(false)
      setNewTemplate({ name: '', description: '', category: 'general' })
      fetchData()

    } catch (error: any) {
      toast({
        title: "Fehler",
        description: error.message,
        variant: "destructive"
      })
    }
  }

  const openTemplateEditor = (template: Template) => {
    setEditingTemplate(template)
    setTemplateForm({
      name: template.name,
      description: template.description || '',
      category: template.category,
      is_active: template.is_active,
      preview_image_url: template.preview_image_url || '',
      template_data: JSON.stringify(template.template_data, null, 2)
    })
    setIsEditTemplateOpen(true)
  }

  const saveTemplate = async () => {
    if (!editingTemplate) return

    try {
      // Validate JSON
      let templateData = {}
      try {
        templateData = JSON.parse(templateForm.template_data)
      } catch (e) {
        throw new Error('Ungültiges JSON Format im Template Data Feld')
      }

      const { error } = await supabase
        .from('landing_page_templates')
        .update({
          name: templateForm.name,
          description: templateForm.description,
          category: templateForm.category,
          is_active: templateForm.is_active,
          preview_image_url: templateForm.preview_image_url,
          template_data: templateData,
          updated_at: new Date().toISOString()
        })
        .eq('id', editingTemplate.id)

      if (error) throw error

      toast({
        title: "Template aktualisiert",
        description: "Das Template wurde erfolgreich aktualisiert."
      })

      setIsEditTemplateOpen(false)
      setEditingTemplate(null)
      fetchData()

    } catch (error: any) {
      toast({
        title: "Fehler",
        description: error.message,
        variant: "destructive"
      })
    }
  }

  const openPageEditor = (page: LandingPage) => {
    setEditingPage(page)
    setPageBuffer({
      name: page.name,
      slug: page.slug,
      template_id: page.template_id,
      referrer_message: page.referrer_message,
      is_active: page.is_active,
      content_data: page.content_data || {}
    })
    setIsEditPageOpen(true)
  }

  const savePage = async () => {
    if (!editingPage) return

    try {
      const { error } = await supabase
        .from('tenant_landing_pages')
        .update({
          name: pageBuffer.name,
          slug: pageBuffer.slug,
          template_id: pageBuffer.template_id,
          referrer_message: pageBuffer.referrer_message,
          is_active: pageBuffer.is_active,
          content_data: pageBuffer.content_data,
          updated_at: new Date().toISOString()
        })
        .eq('id', editingPage.id)

      if (error) throw error

      toast({
        title: "Landing Page aktualisiert",
        description: "Die Landing Page wurde erfolgreich aktualisiert."
      })

      setIsEditPageOpen(false)
      setEditingPage(null)
      fetchData()

    } catch (error: any) {
      toast({
        title: "Fehler",
        description: error.message,
        variant: "destructive"
      })
    }
  }

  const togglePageStatus = async (pageId: string, currentStatus: boolean) => {
    try {
      const { error } = await supabase
        .from('tenant_landing_pages')
        .update({ is_active: !currentStatus })
        .eq('id', pageId)

      if (error) throw error

      toast({
        title: "Status aktualisiert",
        description: `Landing Page wurde ${!currentStatus ? 'aktiviert' : 'deaktiviert'}.`
      })

      fetchData()

    } catch (error: any) {
      toast({
        title: "Fehler",
        description: error.message,
        variant: "destructive"
      })
    }
  }

  const getStatusBadge = (isActive: boolean, isDefault: boolean = false) => {
    if (isDefault) {
      return (
        <Badge className="bg-purple-100 text-purple-800 hover:bg-purple-100">
          Standard
        </Badge>
      )
    }
    
    return (
      <Badge className={isActive ? 
        "bg-green-100 text-green-800 hover:bg-green-100" : 
        "bg-gray-100 text-gray-800 hover:bg-gray-100"
      }>
        {isActive ? 'Aktiv' : 'Inaktiv'}
      </Badge>
    )
  }

  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    return date.toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    })
  }

  return (
    <DashboardLayout>
      <div className="p-6 space-y-6">
        {/* Header */}
        <div>
          <h2 className="text-3xl font-bold tracking-tight">System Landing Pages</h2>
          <p className="text-muted-foreground">
            Zentrale Verwaltung aller Landing Pages und Templates
          </p>
        </div>

        {/* Stats Cards */}
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-6">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                Gesamt Pages
              </CardTitle>
              <FileText className="h-4 w-4 text-blue-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.totalPages}</div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                Aktive Pages
              </CardTitle>
              <Globe className="h-4 w-4 text-green-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.activePages}</div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                Templates
              </CardTitle>
              <Edit className="h-4 w-4 text-purple-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.totalTemplates}</div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                Unternehmen
              </CardTitle>
              <Building2 className="h-4 w-4 text-orange-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.totalTenants}</div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                Bäume Gepflanzt
              </CardTitle>
              <Trees className="h-4 w-4 text-green-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.treesPlanted.toLocaleString()}</div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                Spenden Gesamt
              </CardTitle>
              <Heart className="h-4 w-4 text-red-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">€{stats.donationsTotal.toFixed(0)}</div>
            </CardContent>
          </Card>
        </div>

        <Tabs defaultValue="pages" className="space-y-4">
          <TabsList>
            <TabsTrigger value="pages">Landing Pages</TabsTrigger>
            <TabsTrigger value="templates">Templates</TabsTrigger>
          </TabsList>

          <TabsContent value="pages" className="space-y-4">
            {/* Filters */}
            <Card>
              <CardContent className="pt-6">
                <div className="flex flex-col md:flex-row gap-4">
                  <div className="flex-1">
                    <div className="relative">
                      <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                      <Input
                        placeholder="Suchen nach Name, Slug oder Unternehmen..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="pl-8"
                      />
                    </div>
                  </div>
                  
                  <Select value={statusFilter} onValueChange={setStatusFilter}>
                    <SelectTrigger className="w-full md:w-[200px]">
                      <SelectValue placeholder="Status filtern" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Alle Status</SelectItem>
                      <SelectItem value="active">Aktiv</SelectItem>
                      <SelectItem value="inactive">Inaktiv</SelectItem>
                      <SelectItem value="default">Standard</SelectItem>
                    </SelectContent>
                  </Select>

                  <Select value={selectedTenant} onValueChange={setSelectedTenant}>
                    <SelectTrigger className="w-full md:w-[200px]">
                      <SelectValue placeholder="Unternehmen" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Alle Unternehmen</SelectItem>
                      {tenants.map((tenant) => (
                        <SelectItem key={tenant.id} value={tenant.id}>
                          {tenant.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </CardContent>
            </Card>

            {/* Landing Pages Table */}
            <Card>
              <CardHeader>
                <CardTitle>Landing Pages ({filteredPages.length})</CardTitle>
                <CardDescription>
                  Übersicht aller Landing Pages im System
                </CardDescription>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="text-center py-8 text-muted-foreground">
                    Lade Daten...
                  </div>
                ) : filteredPages.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    Keine Landing Pages gefunden
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Name</TableHead>
                          <TableHead>Slug</TableHead>
                          <TableHead>Unternehmen</TableHead>
                          <TableHead>Template</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead>Erstellt</TableHead>
                          <TableHead>Aktionen</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {filteredPages.map((page) => (
                          <TableRow key={page.id}>
                            <TableCell className="font-medium">
                              {page.name}
                            </TableCell>
                            <TableCell className="text-sm font-mono">
                              {page.slug}
                            </TableCell>
                            <TableCell>{page.tenant?.name || 'N/A'}</TableCell>
                            <TableCell>{page.template?.name || 'Standard'}</TableCell>
                            <TableCell>
                              {getStatusBadge(page.is_active, page.is_default)}
                            </TableCell>
                            <TableCell className="text-sm text-muted-foreground">
                              {formatDate(page.created_at)}
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-2">
                                <Button
                                  variant="outline"
                                  size="sm"
                  onClick={() => navigate(`/superadmin/builder/${page.id}`)}
                                >
                                  <Edit className="h-4 w-4" />
                                </Button>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => togglePageStatus(page.id, page.is_active)}
                                >
                                  {page.is_active ? 'Deaktivieren' : 'Aktivieren'}
                                </Button>
                                <Button variant="outline" size="sm">
                                  <Eye className="h-4 w-4" />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="templates" className="space-y-4">
            {/* Templates Header */}
            <div className="flex justify-between items-center">
              <div>
                <h3 className="text-lg font-medium">Landing Page Templates</h3>
                <p className="text-sm text-muted-foreground">
                  Verwalten Sie Templates für Landing Pages
                </p>
              </div>
              
              <Dialog open={isCreateTemplateOpen} onOpenChange={setIsCreateTemplateOpen}>
                <DialogTrigger asChild>
                  <Button>
                    <Plus className="h-4 w-4 mr-2" />
                    Template erstellen
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Neues Template erstellen</DialogTitle>
                    <DialogDescription>
                      Erstellen Sie ein neues Landing Page Template für Ihre Tenants.
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4">
                    <div>
                      <Input
                        placeholder="Template Name"
                        value={newTemplate.name}
                        onChange={(e) => setNewTemplate({...newTemplate, name: e.target.value})}
                      />
                    </div>
                    <div>
                      <Textarea
                        placeholder="Beschreibung"
                        value={newTemplate.description}
                        onChange={(e) => setNewTemplate({...newTemplate, description: e.target.value})}
                      />
                    </div>
                    <div>
                      <Select 
                        value={newTemplate.category} 
                        onValueChange={(value) => setNewTemplate({...newTemplate, category: value})}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Kategorie" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="general">Allgemein</SelectItem>
                          <SelectItem value="restaurant">Restaurant</SelectItem>
                          <SelectItem value="retail">Einzelhandel</SelectItem>
                          <SelectItem value="service">Dienstleistung</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <Button onClick={createTemplate} className="w-full">
                      Template erstellen
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            </div>

            {/* Templates Grid */}
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {templates.map((template) => (
                <Card key={template.id}>
                  <CardHeader>
                    <div className="flex justify-between items-start">
                      <div>
                        <CardTitle className="text-lg">{template.name}</CardTitle>
                        <CardDescription>{template.description}</CardDescription>
                      </div>
                      <Badge variant="secondary">
                        {template.category}
                      </Badge>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="flex justify-between items-center">
                      <Badge className={template.is_active ? 
                        "bg-green-100 text-green-800 hover:bg-green-100" : 
                        "bg-gray-100 text-gray-800 hover:bg-gray-100"
                      }>
                        {template.is_active ? 'Aktiv' : 'Inaktiv'}
                      </Badge>
                      <Button 
                        variant="outline" 
                        size="sm"
        onClick={() => navigate(`/superadmin/template-builder/${template.id}`)}
                      >
                        <Edit className="h-4 w-4 mr-2" />
                        Bearbeiten
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Template Edit Dialog */}
      <Dialog open={isEditTemplateOpen} onOpenChange={setIsEditTemplateOpen}>
        <DialogContent className="max-w-6xl max-h-[90vh] overflow-hidden">
          <DialogHeader>
            <DialogTitle>Template bearbeiten</DialogTitle>
            <DialogDescription>
              Bearbeiten Sie die Template-Eigenschaften und Konfiguration.
            </DialogDescription>
          </DialogHeader>
          
          <Tabs defaultValue="builder" className="flex-1 overflow-hidden">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="builder">🎨 Visual Builder</TabsTrigger>
              <TabsTrigger value="settings">⚙️ Einstellungen</TabsTrigger>
              <TabsTrigger value="json">📄 JSON Editor</TabsTrigger>
            </TabsList>

            <TabsContent value="builder" className="flex-1 -mx-6 -mb-6">
              <div className="h-[75vh]">
                {editingTemplate && (
                  <ErrorBoundary>
                    <VisualPageBuilder
                      initialData={(() => {
                        try {
                          return JSON.parse(templateForm.template_data)
                        } catch {
                          return undefined
                        }
                      })()}
                      onSave={(data) => 
                        setTemplateForm(prev => ({
                          ...prev, 
                          template_data: JSON.stringify(data, null, 2)
                        }))
                      }
                      onPreview={(data) => console.log('Template Preview:', data)}
                    />
                  </ErrorBoundary>
                )}
              </div>
            </TabsContent>

            <TabsContent value="settings" className="space-y-4 max-h-[60vh] overflow-y-auto">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="template-name">Name</Label>
                  <Input
                    id="template-name"
                    value={templateForm.name}
                    onChange={(e) => setTemplateForm({...templateForm, name: e.target.value})}
                  />
                </div>
                <div>
                  <Label htmlFor="template-category">Kategorie</Label>
                  <Select 
                    value={templateForm.category} 
                    onValueChange={(value) => setTemplateForm({...templateForm, category: value})}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-background border border-border z-50">
                      <SelectItem value="general">Allgemein</SelectItem>
                      <SelectItem value="restaurant">Restaurant</SelectItem>
                      <SelectItem value="retail">Einzelhandel</SelectItem>
                      <SelectItem value="service">Dienstleistung</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label htmlFor="template-description">Beschreibung</Label>
                <Textarea
                  id="template-description"
                  value={templateForm.description}
                  onChange={(e) => setTemplateForm({...templateForm, description: e.target.value})}
                />
              </div>

              <div>
                <Label htmlFor="template-image">Vorschaubild URL</Label>
                <Input
                  id="template-image"
                  value={templateForm.preview_image_url}
                  onChange={(e) => setTemplateForm({...templateForm, preview_image_url: e.target.value})}
                  placeholder="https://example.com/preview.jpg"
                />
              </div>

              <div className="flex items-center space-x-2">
                <Switch
                  id="template-active"
                  checked={templateForm.is_active}
                  onCheckedChange={(checked) => setTemplateForm({...templateForm, is_active: checked})}
                />
                <Label htmlFor="template-active">Template aktiv</Label>
              </div>

              <div className="flex justify-end gap-2">
                <Button 
                  variant="outline" 
                  onClick={() => setIsEditTemplateOpen(false)}
                >
                  Abbrechen
                </Button>
                <Button onClick={saveTemplate}>
                  Speichern
                </Button>
              </div>
            </TabsContent>

            <TabsContent value="json" className="space-y-4 max-h-[60vh] overflow-y-auto">
              <div>
                <Label htmlFor="template-data">Template Data (JSON)</Label>
                <Textarea
                  id="template-data"
                  value={templateForm.template_data}
                  onChange={(e) => setTemplateForm({...templateForm, template_data: e.target.value})}
                  rows={15}
                  className="font-mono text-sm"
                  placeholder='{"blocks": [], "settings": {}}'
                />
              </div>
              <div className="flex justify-end gap-2">
                <Button 
                  variant="outline" 
                  onClick={() => setIsEditTemplateOpen(false)}
                >
                  Abbrechen
                </Button>
                <Button onClick={saveTemplate}>
                  Speichern
                </Button>
              </div>
            </TabsContent>
          </Tabs>
        </DialogContent>
      </Dialog>

      {/* Page Edit Dialog */}
      <Dialog open={isEditPageOpen} onOpenChange={setIsEditPageOpen}>
        <DialogContent className="max-w-6xl max-h-[90vh] overflow-hidden">
          <DialogHeader>
            <DialogTitle>Landing Page bearbeiten</DialogTitle>
            <DialogDescription>
              Bearbeiten Sie die Landing Page Inhalte und Einstellungen.
            </DialogDescription>
          </DialogHeader>
          
          <Tabs defaultValue="builder" className="flex-1 overflow-hidden">
            <TabsList className="grid w-full grid-cols-4">
              <TabsTrigger value="builder">🎨 Visual Builder</TabsTrigger>
              <TabsTrigger value="content">📝 Content Editor</TabsTrigger>
              <TabsTrigger value="settings">⚙️ Einstellungen</TabsTrigger>
              <TabsTrigger value="preview">👁️ Live Vorschau</TabsTrigger>
            </TabsList>

            <TabsContent value="builder" className="flex-1 -mx-6 -mb-6">
              <div className="h-[75vh]">
                {editingPage && (
                <ErrorBoundary>
                  <VisualPageBuilder
                    initialData={pageBuffer.content_data || undefined}
                    onSave={(data) => 
                      setPageBuffer(prev => ({
                        ...prev, 
                        content_data: data
                      }))
                    }
                    onPreview={(data) => console.log('Preview:', data)}
                  />
                </ErrorBoundary>
                )}
              </div>
            </TabsContent>

            <TabsContent value="content" className="space-y-4 max-h-[60vh] overflow-y-auto">
              {editingPage && (
                <ContentEditor
                  contentData={pageBuffer.content_data || {}}
                  onUpdate={(updatedContent) => 
                    setPageBuffer(prev => ({
                      ...prev, 
                      content_data: updatedContent
                    }))
                  }
                />
              )}
            </TabsContent>

            <TabsContent value="settings" className="space-y-4 max-h-[60vh] overflow-y-auto">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="page-name">Name</Label>
                  <Input
                    id="page-name"
                    value={pageBuffer.name || ''}
                    onChange={(e) => setPageBuffer({...pageBuffer, name: e.target.value})}
                  />
                </div>
                <div>
                  <Label htmlFor="page-slug">Slug</Label>
                  <Input
                    id="page-slug"
                    value={pageBuffer.slug || ''}
                    onChange={(e) => setPageBuffer({...pageBuffer, slug: e.target.value})}
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="page-template">Template</Label>
                <Select 
                  value={pageBuffer.template_id || ''} 
                  onValueChange={(value) => setPageBuffer({...pageBuffer, template_id: value})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Template auswählen" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="">Standard</SelectItem>
                    {templates.filter(t => t.is_active).map((template) => (
                      <SelectItem key={template.id} value={template.id}>
                        {template.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="referrer-message">Referrer Nachricht</Label>
                <Input
                  id="referrer-message"
                  value={pageBuffer.referrer_message || ''}
                  onChange={(e) => setPageBuffer({...pageBuffer, referrer_message: e.target.value})}
                  placeholder="Gerade hat {referrer_name} an deinen Geldbeutel gedacht."
                />
              </div>

              <div className="flex items-center space-x-2">
                <Switch
                  id="page-active"
                  checked={pageBuffer.is_active || false}
                  onCheckedChange={(checked) => setPageBuffer({...pageBuffer, is_active: checked})}
                />
                <Label htmlFor="page-active">Page aktiv</Label>
              </div>

              <div className="flex justify-end gap-2">
                <Button 
                  variant="outline" 
                  onClick={() => setIsEditPageOpen(false)}
                >
                  Abbrechen
                </Button>
                <Button onClick={savePage}>
                  Speichern
                </Button>
              </div>
            </TabsContent>

            <TabsContent value="content" className="flex-1 overflow-hidden">
              <div className="h-[60vh]">
                {editingPage && (
                  <ContentEditor 
                    contentData={pageBuffer.content_data || {}}
                    onUpdate={(updatedContent) => 
                      setPageBuffer({...pageBuffer, content_data: updatedContent})
                    }
                  />
                )}
              </div>
            </TabsContent>

            <TabsContent value="preview" className="flex-1 overflow-hidden">
              <div className="h-[60vh] overflow-y-auto">
                {editingPage && (
                  <LivePreview
                    contentData={pageBuffer.content_data || {}}
                    tenantSlug={editingPage.tenant?.name?.toLowerCase().replace(/\s+/g, '-') || 'demo'}
                    pageSlug={pageBuffer.slug || editingPage.slug}
                    referrerMessage={pageBuffer.referrer_message || ''}
                  />
                )}
              </div>
            </TabsContent>
          </Tabs>
        </DialogContent>
      </Dialog>

    </DashboardLayout>
  )
}

export default SystemLandingPages